import { Clear, Search } from "@mui/icons-material";
import { IconButton, InputAdornment, SxProps, TextField, Theme } from "@mui/material";
import { ChangeEvent, PropsWithChildren, useEffect, useRef, useState, type JSX } from 'react';
import useDebounce from "../../hooks/use-debounce/UseDebounce";

export type NavigationFilterInputProps = {
    id?: string;
    value: string;
    debounceDelay?: number;
    autoFocus?: boolean;
    sx?: SxProps<Theme>
    onChange?: (value: string) => void;
    stopPropagation?: boolean
};

export default function NavigationFilterInput({
    id,
    value,
    debounceDelay = 500,
    autoFocus,
    sx,
    onChange,
    stopPropagation
}: PropsWithChildren<NavigationFilterInputProps>): JSX.Element {
    const [filter, setFilter] = useState(value ?? "");
    const debouncedFilter = useDebounce(filter, debounceDelay);
    const filterChangeRef = useRef(onChange);

    useEffect(() => {
        filterChangeRef.current = onChange;
    }, [onChange]);

    // Using onFilterValueChange directly requires it to be added
    // to the dependency array, which causes recursive renders.
    useEffect(() => {
        filterChangeRef.current?.(debouncedFilter);
    }, [debouncedFilter]);

    useEffect(() => {
        setFilter(value ?? "");
    }, [value]);

    return (
        <TextField
            id={id}
            variant="outlined"
            size="small"
            placeholder="Filter"
            onKeyDown={stopPropagation ? (e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                } : undefined
            }
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setFilter(event.target.value);
            }}
            value={filter}
            sx={sx}
            inputRef={ref => {
                if (autoFocus && ref) {
                    setTimeout(() => {
                        ref?.focus();
                    }, 0);
                }
            }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={() => setFilter("")}
                                disabled={!filter}
                            >
                                <Clear />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: {
                        paddingLeft: 1,
                        paddingRight: 1,
                    }
                },
                inputLabel: {
                    shrink: true,
                }
            }}
        />
    );
}